import CustomCard from '@/components/customCard';
import RegularText from '@/components/RegularText';
import { LocationConditionEnum } from '@/constants/enum';
import { uniq } from '@/helpers/string';
import useUserPlan from '@/hooks/useUserPlan';
import { ZipcodeConditionStyled } from '@/pages/Settings/Components/EstimateRules/components/CURule/components/DisplayConditions/components/ZipcodeCondition/styled';
import checkoutRuleSlice, {
  checkoutRuleErrorsSelector,
  checkoutRuleSettingsSelector,
  currentOrderItemValueSelector,
} from '@/redux/slice/checkoutRule.slice';
import ruleSlice, { ruleErrorsSelector, ruleSettingsSelector } from '@/redux/slice/rule.slice';
import { Badge, InlineStack, Scrollable, TextField } from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { Tone } from '@shopify/polaris/build/ts/src/components/Badge';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConnectBadge from '../ConnectBadge';

interface IProps {
  onDismiss: () => void;
  badgeTone?: Tone;
  hasConnectBadge?: boolean;
  badgeContent?: string;
  isInCheckout?: boolean;
}

const CityCondition = (props: IProps) => {
  const dispatch = useDispatch();

  const currentOrderItemValue = useSelector(currentOrderItemValueSelector);
  const dataNormalRule = useSelector(ruleSettingsSelector);
  const dataCheckoutRule = useSelector(checkoutRuleSettingsSelector);
  const dataRuleLocations = useMemo(() => {
    return props.isInCheckout ? dataCheckoutRule.checkoutLocationConditions : dataNormalRule.locationConditions;
  }, [dataCheckoutRule, dataNormalRule, props.isInCheckout]);

  const errorsNormal = useSelector(ruleErrorsSelector);
  const errorsCheckout = useSelector(checkoutRuleErrorsSelector);

  const errors = useMemo(() => {
    return props.isInCheckout ? errorsCheckout : errorsNormal;
  }, [errorsCheckout, errorsNormal, props.isInCheckout]);

  const { isPlanLimited } = useUserPlan();

  const [state, setState] = useState('');

  const currentSlice = useMemo(() => {
    return props.isInCheckout ? checkoutRuleSlice : ruleSlice;
  }, [props.isInCheckout]);

  const cityList = useMemo(() => {
    return uniq(state.split(',').map((item) => item.trim()))?.filter((item) => item !== '');
  }, [state]);

  const handleSetError = useCallback(
    (value: string) => {
      dispatch(
        currentSlice.actions.handleSetError({
          //@ts-ignore-next-line
          value: { field: 'city', msg: value },
        }),
      );
    },
    [currentSlice.actions, dispatch],
  );

  const handleUpdateRule = useCallback(
    (value: string) => {
      const indexOfOldCity = dataRuleLocations.findIndex((item) => item.type === LocationConditionEnum.City);
      const newDataLocationConditions = [...dataRuleLocations];
      if (indexOfOldCity !== -1) {
        const current = newDataLocationConditions[indexOfOldCity];
        newDataLocationConditions.splice(indexOfOldCity, 1, {
          type: LocationConditionEnum.City,
          value: [
            {
              data: value,
            },
          ],
          order: current?.order ?? currentOrderItemValue,
        });
        if (!current?.order || Number.isNaN(+current?.order)) {
          //@ts-expect-error
          if (props.isInCheckout) dispatch(currentSlice.actions.handleAddCurrentCount());
        }
      }
      dispatch(
        currentSlice.actions.handleChange({
          //@ts-ignore-next-line
          key: props.isInCheckout ? 'checkoutLocationConditions' : 'locationConditions',
          value: newDataLocationConditions,
        }),
      );

      handleSetError(value.length ? '' : 'Please enter at least 1 city');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSlice.actions, JSON.stringify(dataRuleLocations), dispatch, handleSetError, props.isInCheckout],
  );

  const onRemoveCity = (city: string) => {
    const listCity = [...state.split(',')];
    const newListCity = listCity?.filter((item) => {
      return item.trim() !== city;
    });
    setState(newListCity.join(','));
    const temp = uniq(newListCity.map((item) => item.trim()))?.filter((item) => item !== '');
    handleUpdateRule(temp?.toString());
  };

  const getCityCondition = useCallback(() => {
    if (dataRuleLocations) {
      const cityCondition = dataRuleLocations.find((item) => item.type === LocationConditionEnum.City)?.value[0].data;
      return cityCondition;
    }
    return null;
  }, [dataRuleLocations]);

  useEffect(() => {
    const dataCity = getCityCondition();
    if (dataCity) {
      setState(dataCity);
    }
  }, [getCityCondition]);

  return (
    <ZipcodeConditionStyled>
      {!!props.hasConnectBadge && <ConnectBadge tone={props.badgeTone}>{props.badgeContent}</ConnectBadge>}
      <CustomCard
        title={
          <div>
            <span className="mr-8">Cities</span>
            <Badge>{cityList.length + ''}</Badge>
          </div>
        }
        textClassName="bold-text-styled"
        secondaryActions={{
          onAction: props.onDismiss,
          buttonProps: {
            icon: DeleteIcon,
            tone: 'critical',
            variant: 'plain',
          },
        }}
        collapsible
        defaultExpanded
      >
        <div className="mt-8">
          <RegularText>Enter city name separated by comma (,).</RegularText>
        </div>
        <div className="mt-8">
          <TextField
            error={errors?.find((item) => item.field === 'city')?.msg || ''}
            multiline={2}
            maxHeight={'500px'}
            label=""
            labelHidden
            value={state}
            onChange={(value) => {
              setState(value);
              handleSetError('');
            }}
            autoComplete="off"
            onBlur={() => {
              handleUpdateRule(cityList.toString());
            }}
            disabled={isPlanLimited}
          />
          <Scrollable className="post-tags" focusable>
            <InlineStack gap={'100'} wrap={true}>
              {uniq(state.split(',').map((item) => item.trim()))
                ?.filter((item) => item !== '')
                .splice(0, 500)
                .map((city) => {
                  return (
                    <div className="mt-8 mr-8 Polaris-Tag" key={city}>
                      <h1 title={city} className="Polaris-Tag__TagText">
                        {city}
                      </h1>
                      <button className="Polaris-Tag__Button" onClick={() => onRemoveCity(city)}>
                        <div>X</div>
                      </button>
                    </div>
                  );
                })}
            </InlineStack>
          </Scrollable>
        </div>
      </CustomCard>
    </ZipcodeConditionStyled>
  );
};

export default CityCondition;
